/* eslint-disable @typescript-eslint/prefer-nullish-coalescing --
 * `||` is load-bearing throughout this file and `??` would be a behavior
 * change. The old backend encodes "not configured" as the NUMBER ZERO, not as
 * null — a merchant with no per-km charge sends `payment_per_km: 0`, and the
 * schema defaults every absent numeric field to 0 as well. `??` only falls
 * through on null/undefined, so it would keep those zeroes and show a free
 * tariff where the original correctly fell back to the next source in the
 * chain. Each `??` below is deliberate too: those fields are genuinely
 * nullable (`discount`, `discount_valid_from`), where 0 and null differ.
 */
import type { LegacyPricing } from '../model/pricing-schema';

/**
 * Flattens the pricing payload into the view model the tariffs screen renders —
 * a 1-to-1 port of the old app's `getPricingInfo`.
 *
 * The complexity is real, not incidental: a merchant is either on the standard
 * `base_rate` or on a negotiated `tariff`, and when a named tariff exists it
 * overrides most (not all) of the base rate. The old code expressed that as a
 * chain of `||` fallbacks per field; the same precedence is preserved here.
 *
 * One deliberate difference: the original read two spellings of the same key
 * (`pricing.tarrif?.name` alongside `tariff.name`) to cope with a backend typo
 * that no longer appears on the wire. That fallback is dropped — the schema
 * would reject an unknown shape at the boundary anyway, which is where a
 * regression should surface.
 */
export interface LegacyTariffView {
  hasTariff: boolean;
  /** Distance bands are shown only when at least one band has a price. */
  hasTariffDynamicPricing: boolean;
  /** A flat price applies when there are no priced bands. */
  hasTariffFixedPrice: boolean;
  name: string | null;
  minSum: number;
  doorToDoor: number;
  perKm: number;
  initialDistance: number;
  initialPrice: number;
  afterInitialDistancePrice: number;
  fixedPrice: number;
  newPoint: number;
  cancelledPaidPrice: number;
  discount: number;
  discountValidFrom: string | null;
  discountValidTill: string | null;
  dynamicPricings: { distance: number; price: number }[];
  freeWaitTime: number;
  waitPrice: number;
  /** Undefined on a named tariff — the coefficient applies to base rates only. */
  timeFrameCoefficient: number | undefined;
}

/** The `new_point` charge a named tariff falls back to when it declares none. */
const DEFAULT_TARIFF_NEW_POINT = 5000;

export function toLegacyTariffView(pricing: LegacyPricing | undefined): LegacyTariffView {
  const tariff = pricing?.tariff ?? null;
  const hasTariff = tariff !== null;
  const base = pricing?.base_rate;
  const dynamicPricings = base?.dynamic_pricings ?? [];

  // Only bands with a price are meaningful; the backend pads the list with
  // zero-priced upper bounds (e.g. `{distance: 50000, price: 0}`).
  const hasTariffDynamicPricing =
    hasTariff && dynamicPricings.some((item) => item.price > 0);

  const fixedPrice = base?.fixed_price || tariff?.price_after_range || 0;
  const hasTariffFixedPrice = hasTariff && !hasTariffDynamicPricing && fixedPrice > 0;

  return {
    hasTariff,
    hasTariffDynamicPricing,
    hasTariffFixedPrice,
    // The old fallback chain verbatim — including the backend's `tarrif` typo
    // sibling and the base rate's own name. `null` at the end means the screen
    // renders its `tariff.base` label.
    name: tariff?.name || pricing?.tarrif?.name || base?.name || null,
    minSum: base?.courier_call ?? 0,
    doorToDoor: base?.door_to_door ?? 0,
    perKm: base?.payment_per_km ?? 0,
    initialDistance: tariff?.range ?? 0,
    initialPrice: tariff?.price ?? 0,
    afterInitialDistancePrice:
      tariff?.price_per_km_after_range || tariff?.price_after_range || 0,
    fixedPrice,
    newPoint: base?.new_point || (hasTariff ? DEFAULT_TARIFF_NEW_POINT : 0),
    cancelledPaidPrice: base?.cancelled_paid_price || tariff?.cancelled_paid_price || 0,
    discount: pricing?.discount ?? tariff?.discount ?? 0,
    discountValidFrom:
      pricing?.discount_valid_from ?? tariff?.discount_valid_from ?? null,
    discountValidTill:
      pricing?.discount_valid_till ?? tariff?.discount_valid_till ?? null,
    // Bands are shown ascending; the backend already returns them that way,
    // but the screen depends on it for its "from X to Y" labels.
    dynamicPricings: [...dynamicPricings].sort((a, b) => a.distance - b.distance),
    freeWaitTime: hasTariff
      ? tariff?.free_wait_time || pricing?.wait_time.free_wait_time || 0
      : (pricing?.wait_time.free_wait_time ?? 0),
    waitPrice: hasTariff
      ? tariff?.price_per_minute_after_wait ||
        pricing?.wait_time.price_per_minute_after ||
        0
      : (pricing?.wait_time.price_per_minute_after ?? 0),
    // A named tariff is a fixed negotiated price, so surge never applies.
    timeFrameCoefficient: hasTariff ? undefined : pricing?.time_frame_coefficient,
  };
}

/** Metres → a short km label (`7000` → `7`, `7500` → `7.5`). */
export function legacyDistanceKm(distance: number): string {
  const km = distance / 1000;
  return Number.isInteger(km) ? String(km) : String(Number(km.toFixed(1)));
}

/** The demand multiplier for display: basis points → `3` / `2.5`. */
export function legacyCoefficient(coefficient: number): string {
  return (coefficient / 100).toFixed(2).replace(/0$/, '');
}
